import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { SITE_TYPES, getSiteTypeAssetName } from "../utils/siteTypes";

const MIN_MARKER_SIZE = 16;
const MAX_MARKER_SIZE = 48;

type VehicleAssetSpec = {
  key: string;
  label: string;
  hint: string;
};

const VEHICLE_ASSETS: VehicleAssetSpec[] = [
  { key: "Vehicle_IsMoving", label: "Moving icon", hint: "Shown when a vehicle is in motion" },
  { key: "Vehicle_NotMoving", label: "Stopped icon", hint: "Shown when a vehicle is stationary" },
];

const DEFAULT_SITE_ASSET_KEY = "SiteIcon";

type AssetRecord = { name: string; url?: string | null };

function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 24;
  return Math.max(MIN_MARKER_SIZE, Math.min(MAX_MARKER_SIZE, Math.round(value)));
}

function shallowEqualNumbers(a: Record<string, number>, b: Record<string, number>) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) if (a[k] !== b[k]) return false;
  return true;
}

export default function MapAssetsManager({ onClose }: { onClose: () => void }) {
  const allAssets = useQuery((api as any).appAssets.getAll) as AssetRecord[] | undefined;
  const mapDisplaySettings = useQuery((api as any).appSettings.getMapDisplaySettings) as
    | { siteMarkerSizePx: number; siteMarkerSizes: Record<string, number> }
    | undefined;

  const generateUploadUrl = useMutation((api as any).appAssets.generateUploadUrl);
  const saveAsset = useMutation((api as any).appAssets.saveAsset);
  const setSiteMarkerSize = useMutation((api as any).appSettings.setSiteMarkerSize);
  const setSiteMarkerSizeForType = useMutation((api as any).appSettings.setSiteMarkerSizeForType);

  const assetsByName = useMemo(() => {
    const map = new Map<string, AssetRecord>();
    for (const a of allAssets || []) {
      if (a?.name) map.set(a.name, a);
    }
    return map;
  }, [allAssets]);

  const committedDefaultSize = clampSize(mapDisplaySettings?.siteMarkerSizePx ?? 24);
  const committedPerTypeSizes = useMemo(() => {
    const fromServer = mapDisplaySettings?.siteMarkerSizes ?? {};
    const result: Record<string, number> = {};
    for (const type of SITE_TYPES) {
      const v = fromServer[type];
      result[type] = clampSize(typeof v === "number" ? v : committedDefaultSize);
    }
    return result;
  }, [mapDisplaySettings?.siteMarkerSizes, committedDefaultSize]);

  // Local working state ("draft"). Reset whenever the committed values arrive
  // for the first time or change underneath us.
  const [draftDefaultSize, setDraftDefaultSize] = useState<number>(committedDefaultSize);
  const [draftPerTypeSizes, setDraftPerTypeSizes] = useState<Record<string, number>>(committedPerTypeSizes);
  const [stagedFiles, setStagedFiles] = useState<Record<string, File>>({});
  const [stagedPreviewUrls, setStagedPreviewUrls] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (mapDisplaySettings === undefined) return;
    setDraftDefaultSize(committedDefaultSize);
    setDraftPerTypeSizes(committedPerTypeSizes);
    initializedRef.current = true;
  }, [committedDefaultSize, committedPerTypeSizes, mapDisplaySettings]);

  // Revoke staged object URLs when the modal closes.
  useEffect(() => {
    return () => {
      for (const url of Object.values(stagedPreviewUrls)) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    };
    // we intentionally do not list stagedPreviewUrls — we only want this on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const [pickingFor, setPickingFor] = useState<string | null>(null);

  const sizesDirty =
    draftDefaultSize !== committedDefaultSize ||
    !shallowEqualNumbers(draftPerTypeSizes, committedPerTypeSizes);
  const filesDirty = Object.keys(stagedFiles).length > 0;
  const isDirty = sizesDirty || filesDirty;

  const openFilePicker = (assetKey: string) => {
    setPickingFor(assetKey);
    setSaveError(null);
    requestAnimationFrame(() => hiddenInputRef.current?.click());
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const target = pickingFor;
    e.target.value = "";
    setPickingFor(null);
    if (!file || !target) return;

    setStagedFiles((prev) => ({ ...prev, [target]: file }));
    setStagedPreviewUrls((prev) => {
      const next = { ...prev };
      if (next[target]) {
        try {
          URL.revokeObjectURL(next[target]);
        } catch {
          // ignore
        }
      }
      next[target] = URL.createObjectURL(file);
      return next;
    });
  };

  const undoStagedFile = (assetKey: string) => {
    setStagedFiles((prev) => {
      const next = { ...prev };
      delete next[assetKey];
      return next;
    });
    setStagedPreviewUrls((prev) => {
      const next = { ...prev };
      if (next[assetKey]) {
        try {
          URL.revokeObjectURL(next[assetKey]);
        } catch {
          // ignore
        }
      }
      delete next[assetKey];
      return next;
    });
  };

  const resolvePreviewUrl = (assetKey: string): string | null => {
    return stagedPreviewUrls[assetKey] || assetsByName.get(assetKey)?.url || null;
  };

  const handleSave = async () => {
    if (saving || !isDirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      // 1) Upload every staged file, then persist the asset reference.
      for (const [assetKey, file] of Object.entries(stagedFiles)) {
        const uploadUrl = await generateUploadUrl();
        const mimeType = file.type || "application/octet-stream";
        const res = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": mimeType },
          body: file,
        });
        if (!res.ok) {
          throw new Error(`Upload failed for ${assetKey}`);
        }
        const { storageId } = await res.json();
        await saveAsset({ name: assetKey, storageId, mimeType });
      }

      // 2) Persist size changes — only the values that actually changed.
      if (draftDefaultSize !== committedDefaultSize) {
        await setSiteMarkerSize({ siteMarkerSizePx: draftDefaultSize });
      }
      for (const type of SITE_TYPES) {
        if (draftPerTypeSizes[type] !== committedPerTypeSizes[type]) {
          await setSiteMarkerSizeForType({
            siteType: type,
            siteMarkerSizePx: draftPerTypeSizes[type],
          });
        }
      }

      // 3) Clear staging — the server will have re-emitted everything,
      // letting the parent re-render with the new values.
      for (const url of Object.values(stagedPreviewUrls)) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
      setStagedFiles({});
      setStagedPreviewUrls({});
      onClose();
    } catch (e) {
      console.error("Map assets save failed", e);
      setSaveError(e instanceof Error ? e.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (isDirty && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    for (const url of Object.values(stagedPreviewUrls)) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }
    onClose();
  };

  // ESC to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (confirmDiscard) {
          setConfirmDiscard(false);
          return;
        }
        requestClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, confirmDiscard]);

  const fallbackSiteIconUrl = assetsByName.get(DEFAULT_SITE_ASSET_KEY)?.url || "/WebAssets/SiteIcon.png";

  return (
    <div
      className="mam-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        className="mam-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mam-title"
      >
        <header className="mam-header">
          <div className="mam-header-titles">
            <div className="mam-kicker">SETTINGS</div>
            <h2 id="mam-title" className="mam-title">Manage Map Assets</h2>
          </div>
          <button
            type="button"
            className="mam-close"
            onClick={requestClose}
            aria-label="Close"
            title="Close"
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
              <path
                d="M1.25 1.25l5.5 5.5M6.75 1.25l-5.5 5.5"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="mam-body">
          {/* Vehicle markers */}
          <section className="mam-section">
            <div className="mam-section-head">
              <div className="mam-section-label">Vehicle markers</div>
              <div className="mam-section-hint">Used to plot vehicles on the live map.</div>
            </div>
            <ul className="mam-list" role="list">
              {VEHICLE_ASSETS.map((a) => {
                const preview = resolvePreviewUrl(a.key);
                const isStaged = !!stagedFiles[a.key];
                return (
                  <li key={a.key} className={`mam-row${isStaged ? " mam-row--staged" : ""}`}>
                    <div className="mam-row-preview">
                      {preview ? (
                        <img src={preview} alt="" loading="lazy" />
                      ) : (
                        <div className="mam-row-preview-empty" aria-hidden="true">
                          ?
                        </div>
                      )}
                    </div>
                    <div className="mam-row-info">
                      <div className="mam-row-title">{a.label}</div>
                      <div className="mam-row-hint">{a.hint}</div>
                      {isStaged ? (
                        <div className="mam-row-pending">
                          New image staged · saves on Save
                          <button
                            type="button"
                            className="mam-link-btn"
                            onClick={() => undoStagedFile(a.key)}
                          >
                            Undo
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="mam-row-actions">
                      <button
                        type="button"
                        className="mam-btn mam-btn--ghost"
                        onClick={() => openFilePicker(a.key)}
                        disabled={saving}
                      >
                        {preview ? "Replace" : "Upload"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Site markers */}
          <section className="mam-section">
            <div className="mam-section-head">
              <div className="mam-section-label">Site markers</div>
              <div className="mam-section-hint">
                Set a marker image and size for the default fallback and each site type.
              </div>
            </div>
            <ul className="mam-list" role="list">
              {/* Default site marker (with global size) */}
              {(() => {
                const a = { key: DEFAULT_SITE_ASSET_KEY, label: "Default site marker", hint: "Used when a site type has no custom image." };
                const preview = resolvePreviewUrl(a.key) || fallbackSiteIconUrl;
                const isStaged = !!stagedFiles[a.key];
                const sizePx = draftDefaultSize;
                return (
                  <li key={a.key} className={`mam-row mam-row--site${isStaged ? " mam-row--staged" : ""}`}>
                    <div className="mam-row-preview mam-row-preview--site">
                      {preview ? (
                        <img
                          src={preview}
                          alt=""
                          loading="lazy"
                          style={{ width: `${sizePx}px`, height: `${sizePx}px` }}
                        />
                      ) : null}
                    </div>
                    <div className="mam-row-info">
                      <div className="mam-row-title">{a.label}</div>
                      <div className="mam-row-hint">{a.hint}</div>
                      <SizeControl
                        size={sizePx}
                        onChange={(v) => setDraftDefaultSize(clampSize(v))}
                      />
                      {isStaged ? (
                        <div className="mam-row-pending">
                          New image staged · saves on Save
                          <button
                            type="button"
                            className="mam-link-btn"
                            onClick={() => undoStagedFile(a.key)}
                          >
                            Undo
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="mam-row-actions">
                      <button
                        type="button"
                        className="mam-btn mam-btn--ghost"
                        onClick={() => openFilePicker(a.key)}
                        disabled={saving}
                      >
                        {assetsByName.has(a.key) || isStaged ? "Replace" : "Upload"}
                      </button>
                    </div>
                  </li>
                );
              })()}

              {/* Per-type site markers */}
              {SITE_TYPES.map((type) => {
                const assetKey = getSiteTypeAssetName(type);
                const sizePx = draftPerTypeSizes[type] ?? draftDefaultSize;
                const previewStaged = stagedPreviewUrls[assetKey];
                const previewSaved = assetsByName.get(assetKey)?.url;
                const hasCustom = !!previewStaged || !!previewSaved;
                const preview = previewStaged || previewSaved || fallbackSiteIconUrl;
                const isStaged = !!stagedFiles[assetKey];
                return (
                  <li
                    key={assetKey}
                    className={`mam-row mam-row--site${isStaged ? " mam-row--staged" : ""}`}
                  >
                    <div className="mam-row-preview mam-row-preview--site">
                      <img
                        src={preview}
                        alt=""
                        loading="lazy"
                        style={{ width: `${sizePx}px`, height: `${sizePx}px` }}
                      />
                    </div>
                    <div className="mam-row-info">
                      <div className="mam-row-title">{type}</div>
                      <div className="mam-row-hint">
                        {hasCustom
                          ? `Using ${isStaged ? "newly selected image" : "uploaded image"}`
                          : "Falls back to the default site marker"}
                      </div>
                      <SizeControl
                        size={sizePx}
                        onChange={(v) =>
                          setDraftPerTypeSizes((prev) => ({
                            ...prev,
                            [type]: clampSize(v),
                          }))
                        }
                      />
                      {isStaged ? (
                        <div className="mam-row-pending">
                          New image staged · saves on Save
                          <button
                            type="button"
                            className="mam-link-btn"
                            onClick={() => undoStagedFile(assetKey)}
                          >
                            Undo
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="mam-row-actions">
                      <button
                        type="button"
                        className="mam-btn mam-btn--ghost"
                        onClick={() => openFilePicker(assetKey)}
                        disabled={saving}
                      >
                        {hasCustom ? "Replace" : "Upload"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>

        {saveError ? (
          <div className="mam-error" role="alert">
            {saveError}
          </div>
        ) : null}

        <footer className="mam-footer">
          <div className="mam-footer-meta">
            {isDirty ? "Unsaved changes" : "All changes saved"}
          </div>
          <div className="mam-footer-actions">
            <button
              type="button"
              className="mam-btn mam-btn--primary"
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </footer>

        {confirmDiscard ? (
          <div className="mam-discard">
            <div className="mam-discard-text">
              You have unsaved changes. Discard and close?
            </div>
            <div className="mam-discard-actions">
              <button
                type="button"
                className="mam-btn mam-btn--ghost"
                onClick={() => setConfirmDiscard(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="mam-btn mam-btn--danger"
                onClick={() => {
                  setConfirmDiscard(false);
                  // Re-run requestClose with the dirty check now bypassed.
                  for (const url of Object.values(stagedPreviewUrls)) {
                    try {
                      URL.revokeObjectURL(url);
                    } catch {
                      // ignore
                    }
                  }
                  onClose();
                }}
              >
                Discard
              </button>
            </div>
          </div>
        ) : null}

        <input
          ref={hiddenInputRef}
          type="file"
          accept="image/*"
          onChange={onFilePicked}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );
}

function SizeControl({
  size,
  onChange,
}: {
  size: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="mam-size">
      <div className="mam-size-head">
        <span className="mam-size-label">Marker size</span>
        <span className="mam-size-value">{size}px</span>
      </div>
      <input
        type="range"
        className="mam-size-slider"
        min={MIN_MARKER_SIZE}
        max={MAX_MARKER_SIZE}
        step={1}
        value={size}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}
